import * as Sentry from '@sentry/browser'
import { defineStore } from 'pinia'

type Extension = {
  eid: string,
  page: string,
  detail: string
}
type ResponseCallback = (response: any) => void

export const useChromeExtensionStore = defineStore('chromeExtension', () => {
  const snackbar = useSnackbar()
  const chromeExtensionPrefs = ref({
    extension: null as Extension | null
  })

  const eid = computed(() => chromeExtensionPrefs.value.extension?.eid || null)

  const extension = () => computed({
    get: () => chromeExtensionPrefs.value.extension,
    set: (value) => {
      chromeExtensionPrefs.value.extension = value
    }
  })

  const login = async () => {
    try {
      if (!eid.value) { return }
      const { getEffectiveAuthTokenRaw } = useEffectiveAuthToken()
      const token = await getEffectiveAuthTokenRaw()
      if (token) {
        chrome.runtime.sendMessage(eid.value, { greeting: 'emissary-login', jwt: token })
      }
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Error during login to Chrome Extension')
    }
  }

  const logout = () => {
    chromeExtensionPrefs.value.extension = null
  }

  const sendCurrentPage = (currentPage: string) => {
    if (!eid.value) { return }
    chrome.runtime.sendMessage(eid.value, { greeting: 'emissary-set-current-page', currentPage })
  }

  const askForCampaignData = (callback: ResponseCallback) => {
    if (!eid.value) { return }
    chrome.runtime.sendMessage(eid.value, { greeting: 'get-campaign-data' }, {}, callback)
  }

  const getCroppedScreenshot = async (callback: ResponseCallback) => {
    try {
      if (!chromeExtensionPrefs.value.extension?.eid) { return }
      await chrome.runtime.sendMessage(chromeExtensionPrefs.value.extension?.eid, { greeting: 'get-screenshot' }, {}, callback)
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Error getting cropped screenshot from Chrome Extension')
    }
  }

  return {
    editable: {
      extension
    },
    login,
    logout,
    sendCurrentPage,
    askForCampaignData,
    getCroppedScreenshot
  }
})
